import React, { useMemo, useState } from 'react';
import { apiFetch } from '../apiClient';
import { budgetCompare, cellKey, forecastIndex, remainingBand } from './hoursLogic';

const fmt = (h) => Math.round(h).toLocaleString();
const signed = (h) => (Math.round(h) < 0 ? `−${fmt(Math.abs(h))}` : fmt(h));

/**
 * Edit the team-level forecast (TeamMonthlyAllocation, ADR 0027) month by month, with the two limits
 * beside it: the team's open capacity per month (footer) and the project's budget left (right column),
 * both recomputed live from the draft. Saves only the cells you changed, through the same PUT the
 * Forecasting tab uses -- one source of truth, no parallel table.
 */
export default function ForecastEditor({ feed, win, teamIds, includePipeline, nowIdx, canEdit, onSaved }) {
  const [drafts, setDrafts] = useState({});
  const [extra, setExtra] = useState([]);
  const [pick, setPick] = useState({ projectId: '', teamId: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(() => new Set());

  const index = useMemo(() => forecastIndex(feed, { teamIds, includePipeline }), [feed, teamIds, includePipeline]);
  const budget = useMemo(() => budgetCompare(feed, { teamIds, includePipeline, nowIdx }), [feed, teamIds, includePipeline, nowIdx]);
  const months = win.months.map((m) => ({ ...m, i: m.year * 12 + m.month - 1 }));
  const teams = feed.teams.filter((t) => !teamIds.length || teamIds.includes(t.id));
  const teamName = new Map(feed.teams.map((t) => [t.id, t.name]));

  const pairs = new Map(index.pairs);
  for (const e of extra) pairs.set(`${e.projectId}|${e.teamId}`, e);
  const byProject = new Map();
  for (const p of pairs.values()) {
    if (!byProject.has(p.projectId)) byProject.set(p.projectId, []);
    byProject.get(p.projectId).push(p.teamId);
  }
  const projectRows = [...byProject.keys()].filter((id) => index.projects.has(id)).sort((a, b) => index.projects.get(a).name.localeCompare(index.projects.get(b).name));

  const base = (p, t, i) => index.cells.get(cellKey(p, t, i)) || { named: 0, team: 0 };
  const draftOf = (p, t, i) => drafts[cellKey(p, t, i)];
  const value = (p, t, i) => { const d = draftOf(p, t, i); return d !== undefined ? Number(d) || 0 : base(p, t, i).team; };
  const editable = (p, t, i) => canEdit && i > nowIdx && base(p, t, i).named === 0;
  const total = (p, t, i) => base(p, t, i).named + value(p, t, i);

  // Live team demand per month: every cell of the team with drafts swapped in.
  const teamDemand = (t, i) => [...pairs.values()].filter((x) => x.teamId === t && index.projects.has(x.projectId)).reduce((a, x) => a + total(x.projectId, t, i), 0);
  const teamCapacity = (t, i) => {
    const k = months.findIndex((m) => m.i === i);
    return feed.people.filter((p) => p.teamId === t).reduce((a, p) => a + (win.people.find((w) => w.id === p.id)?.capacity[k] || 0), 0);
  };
  // Budget left moves one-for-one with forecast hours edited ahead of today.
  const draftDelta = (p, t) => months.reduce((a, m) => (draftOf(p, t, m.i) !== undefined ? a + value(p, t, m.i) - base(p, t, m.i).team : a), 0);
  const budgetRow = (p) => budget.find((b) => b.project.id === p);
  const leftFor = (p, t) => {
    const b = budgetRow(p);
    const row = t ? b?.teams.find((x) => x.teamId === t) : b;
    if (!row || row.remaining == null) return null;
    const delta = t ? draftDelta(p, t) : (byProject.get(p) || []).reduce((a, tid) => a + draftDelta(p, tid), 0);
    return row.remaining - delta;
  };

  const dirty = Object.keys(drafts).filter((k) => {
    const [p, t, i] = k.split('|');
    return Number(drafts[k]) !== base(p, t, Number(i)).team;
  });
  const bad = dirty.some((k) => !(Number(drafts[k]) >= 0) || drafts[k] === '');

  async function save() {
    setSaving(true);
    setError('');
    try {
      const rows = new Map();
      for (const k of dirty) {
        const [projectId, teamId, i] = k.split('|');
        const r = rows.get(`${projectId}|${teamId}`) || { projectId, teamId, cells: [] };
        r.cells.push({ year: Math.floor(Number(i) / 12), month: (Number(i) % 12) + 1, plannedHours: Number(drafts[k]) });
        rows.set(`${projectId}|${teamId}`, r);
      }
      await apiFetch('/team-allocations', { method: 'PUT', body: JSON.stringify({ rows: [...rows.values()] }) });
      setDrafts({});
      setExtra([]);
      await onSaved();
    } catch (e) {
      setError(e.message || 'Could not save the forecast.');
    } finally {
      setSaving(false);
    }
  }

  const addable = feed.projects.filter((p) => includePipeline || !p.isPipeline);
  const addRow = () => {
    if (!pick.projectId || !pick.teamId) return;
    setExtra((x) => [...x, pick]);
    setOpen((o) => new Set(o).add(pick.projectId));
    setPick({ projectId: '', teamId: '' });
  };
  const toggle = (id) => setOpen((cur) => { const n = new Set(cur); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  return (
    <div className="card">
      <div className="up-banner">
        Edits the team-level forecast (hours per project, per team, per month). Months with hours named to people, and this month and earlier, are read-only. The footer shows each team's open hours and the right column the project's budget left, both live as you type.
      </div>
      <div className="up-actions up-pad">
        <button className="btn btn-outline" onClick={() => setOpen(new Set(projectRows))}>Expand all</button>
        <button className="btn btn-outline" onClick={() => setOpen(new Set())}>Collapse all</button>
        <button className="btn btn-primary" disabled={!dirty.length || bad || saving} onClick={save}>{saving ? 'Saving…' : `Save ${dirty.length || ''} change${dirty.length === 1 ? '' : 's'}`.replace('Save  ', 'Save ')}</button>
        <button className="btn btn-outline" disabled={!dirty.length && !extra.length} onClick={() => { setDrafts({}); setExtra([]); }}>Discard</button>
        {error && <span className="hp-over up-cell">{error}</span>}
      </div>
      <div className="up-actions up-pad">
        <select className="form-select" aria-label="Project to add" value={pick.projectId} onChange={(e) => setPick((p) => ({ ...p, projectId: e.target.value }))}>
          <option value="">Add a project…</option>
          {addable.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <select className="form-select" aria-label="Team to add" value={pick.teamId} onChange={(e) => setPick((p) => ({ ...p, teamId: e.target.value }))}>
          <option value="">for team…</option>
          {feed.teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <button className="btn btn-outline" disabled={!pick.projectId || !pick.teamId} onClick={addRow}>Add row</button>
      </div>
      <div className="up-scroll">
        <table className="up-table">
          <thead>
            <tr><th>Project / team</th>{months.map((m) => <th key={m.i}>{new Date(m.year, m.month - 1, 1).toLocaleString('en-US', { month: 'short', year: '2-digit' })}</th>)}<th>Budget left</th></tr>
          </thead>
          <tbody>
            {projectRows.map((pid) => {
              const isOpen = open.has(pid);
              const left = leftFor(pid);
              return (
                <React.Fragment key={pid}>
                  <tr className="up-team">
                    <th><button className="up-toggle" aria-expanded={isOpen} aria-label={`${isOpen ? 'Collapse' : 'Expand'} ${index.projects.get(pid).name}`} onClick={() => toggle(pid)}><span className="up-chev">{isOpen ? '▾' : '▸'}</span> {index.projects.get(pid).name}</button></th>
                    {months.map((m) => <td key={m.i} className="up-cell">{(() => { const s = byProject.get(pid).reduce((a, t) => a + total(pid, t, m.i), 0); return s ? fmt(s) : ''; })()}</td>)}
                    <td className={`up-cell ${left != null && left < 0 ? 'hp-over' : ''}`}>{left == null ? 'Not set' : signed(left)}</td>
                  </tr>
                  {isOpen && byProject.get(pid).map((tid) => {
                    const l = leftFor(pid, tid);
                    return (
                      <tr key={tid}>
                        <th className="up-indent">{teamName.get(tid)}</th>
                        {months.map((m) => {
                          const b = base(pid, tid, m.i);
                          if (!editable(pid, tid, m.i)) {
                            return <td key={m.i} className="up-cell up-un" title={b.named ? 'Named to people in Forecasting' : m.i <= nowIdx ? 'This month and earlier are closed' : undefined}>{total(pid, tid, m.i) ? fmt(total(pid, tid, m.i)) : ''}</td>;
                          }
                          const d = draftOf(pid, tid, m.i);
                          return (
                            <td key={m.i} className="up-cell" style={{ padding: 0 }}>
                              <input
                                className={`hp-input ${d !== undefined && Number(d) !== b.team ? 'hp-edited' : ''}`}
                                type="number" min="0" step="1" inputMode="decimal"
                                aria-label={`${teamName.get(tid)} ${index.projects.get(pid).name} ${m.year}-${m.month}`}
                                value={d !== undefined ? d : b.team || ''}
                                onChange={(e) => setDrafts((cur) => ({ ...cur, [cellKey(pid, tid, m.i)]: e.target.value }))}
                              />
                            </td>
                          );
                        })}
                        <td className={`up-cell ${l != null && l < 0 ? 'hp-over' : ''}`}>{l == null ? 'Not set' : signed(l)}</td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </tbody>
          <tfoot>
            {teams.map((t) => (
              <tr key={t.id} className="up-team">
                <th>{t.name} — open hours</th>
                {months.map((m) => {
                  const cap = teamCapacity(t.id, m.i);
                  const rem = cap - teamDemand(t.id, m.i);
                  const band = remainingBand(rem, 1, cap);
                  return <td key={m.i} className={`up-cell hp-${band}`} title={`${fmt(teamDemand(t.id, m.i))} h forecast of ${fmt(cap)} h capacity`}>{band === 'none' ? '' : signed(rem)}</td>;
                })}
                <td />
              </tr>
            ))}
          </tfoot>
        </table>
      </div>
    </div>
  );
}
